import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, ButtonProps, Card, Flex, Form, FormItemProps, Tag } from 'antd'
import React, { ReactNode } from 'react'
import { Toolbox } from '~/plugins/toolbox/shared'
import { FormInputType, StepType } from '../../../common/types'
import { useIfConditions } from '../../hooks/useIfConditions'
import { FormItemWithIndications } from './components/FormItem'

type PropsInputDynamic = {
  inputs: FormInputType[]
  propsButtonAdd?: ButtonProps
  propsButtonRemove?: ButtonProps
}

type Props = {
  isReadonly?: boolean
  step: StepType
  formInput: FormInputType<PropsInputDynamic>
  formValuesConfiguration: any
  formValues: any
  onUploadingChange?: (isUploading: boolean) => void
  children: (inputs: FormInputType[], keyDynamic: number) => ReactNode
}

export const ViewerFormDynamic: React.FC<Props> = ({
  isReadonly = false,
  step,
  formInput,
  formValuesConfiguration,
  formValues,
  onUploadingChange,
  children,
}) => {
  const ifConditionsHook = useIfConditions()
  const ifConditions = formInput.ifConditions
  const disabledConditions = formInput.disabledConditions

  const { rules, ...propsFormItem } = formInput.propsFormItem ?? {}

  const { providerAccountIdKey, ...inputProps }: any = {
    disabled: isReadonly,
    ...formInput.propsFormInput,
  }

  const dynamicInputs = formInput.propsFormInput.inputs ?? []

  const isInputHidden = (formValuesCurrent: any, inputValue: any) => {
    const isHidden = isReadonly && Toolbox.Utility.isNull(inputValue)

    if (isHidden) {
      return true
    }

    const formValuesAll = { ...formValues, ...formValuesCurrent }
    const isVisible = ifConditionsHook.run(formValuesAll, ifConditions)

    if (!isVisible) {
      return true
    }

    return false
  }

  const isInputDisabled = (formValuesCurrent: any) => {
    const isDisabled = disabledConditions
      ? ifConditionsHook.run(formValuesCurrent, disabledConditions)
      : inputProps.disabled

    return isDisabled
  }

  const shouldUpdateInput: FormItemProps['shouldUpdate'] = (
    valuesBefore,
    valuesAfter,
  ) => {
    const valueBefore = valuesBefore[step.key]?.[formInput.key]
    const valueAfter = valuesAfter[step.key]?.[formInput.key]
    const hiddenBefore = isInputHidden(valuesBefore, valueBefore)
    const hiddenAfter = isInputHidden(valuesAfter, valueAfter)
    const disabledBefore = isInputDisabled(valuesBefore)
    const disabledAfter = isInputDisabled(valuesAfter)
    const shouldUpdateNow =
      valueBefore !== valueAfter ||
      hiddenBefore !== hiddenAfter ||
      disabledBefore !== disabledAfter

    return shouldUpdateNow
  }

  const { children: childrenButtonRemove, ...propsButtonRemove } =
    inputProps.propsButtonRemove ?? {}

  const { children: childrenButtonAdd, ...propsButtonAdd } =
    inputProps.propsButtonAdd ?? {}

  return (
    <>
      <FormItemWithIndications
        {...propsFormItem}
        shouldUpdate={shouldUpdateInput}
        propsIndication={formInput.propsIndication}
      >
        {options => {
          const value = options.getFieldValue([step.key, formInput.key])
          const values = options.getFieldsValue()
          const isHidden = isInputHidden(values, value)

          if (isHidden) {
            return <></>
          }

          const isDisabled = isInputDisabled(values)

          return (
            <>
              <Form.List {...propsFormItem} name={[step.key, formInput.key]}>
                {(fields, { add, remove }) => (
                  <>
                    <Flex vertical gap={8}>
                      {fields.map(({ key, name, ...restField }) => {
                        const inputs = dynamicInputs.map(input => ({
                          ...input,
                          propsFormItem: {
                            ...restField,
                            ...input.propsFormItem,
                          },
                          propsFormInput: {
                            disabled: isDisabled,
                            ...input.propsFormInput,
                          },
                        }))

                        return (
                          <Card key={key} size="small">
                            <Flex justify="space-between" className="mb-2">
                              <div>
                                <Tag color="default">#{name + 1}</Tag>
                              </div>
                              <Button
                                type="text"
                                title="Remove"
                                {...propsButtonRemove}
                                onClick={() => remove(name)}
                              >
                                <MinusCircleOutlined />
                              </Button>
                            </Flex>

                            {children(inputs, name)}
                          </Card>
                        )
                      })}

                      <Form.Item>
                        <Button
                          type="dashed"
                          onClick={() => add()}
                          block
                          icon={<PlusOutlined />}
                          {...propsButtonAdd}
                        >
                          {childrenButtonAdd ?? 'Add'}
                        </Button>
                      </Form.Item>
                    </Flex>
                  </>
                )}
              </Form.List>
            </>
          )
        }}
      </FormItemWithIndications>
    </>
  )
}
